const CSV_SEPARATOR = ',';

const toBase64 = (value: string): string => {
  return window.btoa(unescape(encodeURIComponent(value)));
};

const fromBase64 = (value: string): string => {
  return decodeURIComponent(escape(window.atob(value)));
};

export const toBase64Url = (value: string): string => {
  return toBase64(value).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

export const fromBase64Url = (value: string): string => {
  const normalized = value.replace(/-/g, '+').replace(/_/g, '/');
  const pad = normalized.length % 4;
  const withPadding = pad === 0 ? normalized : `${normalized}${'='.repeat(4 - pad)}`;
  return fromBase64(withPadding);
};

export const encodeJsonToBase64Url = (value: unknown): string => {
  return toBase64Url(JSON.stringify(value));
};

export const decodeJsonFromBase64Url = <T>(value: string | null | undefined): T | null => {
  if (!value) return null;
  try {
    return JSON.parse(fromBase64Url(value)) as T;
  } catch {
    return null;
  }
};

export const decodeCsv = (value: string | null | undefined): string[] => {
  if (!value) return [];
  return value
    .split(CSV_SEPARATOR)
    .map((item) => item.trim())
    .filter(Boolean);
};

export const encodeCsv = (values: Array<string | null | undefined>): string => {
  return values
    .filter((value): value is string => !!value)
    .map((value) => value.trim())
    .filter(Boolean)
    .join(CSV_SEPARATOR);
};

export const parseEnumParam = <T extends string>(
  value: string | null | undefined,
  allowed: readonly T[],
  fallback: T
): T => {
  if (!value) return fallback;
  return (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
};

export const parseOptionalEnumParam = <T extends string>(
  value: string | null | undefined,
  allowed: readonly T[]
): T | null => {
  if (!value) return null;
  return (allowed as readonly string[]).includes(value) ? (value as T) : null;
};

export const parseIntegerParam = (
  value: string | null | undefined,
  fallback: number,
  min?: number,
  max?: number
): number => {
  if (!value) return fallback;
  const parsed = Number.parseInt(value, 10);
  if (Number.isNaN(parsed)) return fallback;

  const minChecked = typeof min === 'number' ? Math.max(parsed, min) : parsed;
  return typeof max === 'number' ? Math.min(minChecked, max) : minChecked;
};

export const parseFlagParam = (value: string | null | undefined): boolean => {
  return value === '1' || value === 'true';
};

export const stableSearchParamsString = (params: URLSearchParams): string => {
  const entries = Array.from(params.entries()).sort(([a], [b]) => a.localeCompare(b));
  const sorted = new URLSearchParams();
  entries.forEach(([key, value]) => sorted.append(key, value));
  return sorted.toString();
};

export const normalizeSearchString = (search: string): string => {
  const raw = search.startsWith('?') ? search.slice(1) : search;
  if (!raw) return '';
  return stableSearchParamsString(new URLSearchParams(raw));
};
